import { isEqual } from 'lodash';

import { Contract } from '@/types/enums';
import { UserSettings } from '@/apps/common/settings/types/ISettings';

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const getDeepDifferences = (obj1: any, obj2: any) => {
  if (isEqual(obj1, obj2)) {
    return null;
  }

  const differences: Record<string, { old: any; new: any }> = {};
  const allKeys = new Set([
    ...Object.keys(obj1 || {}),
    ...Object.keys(obj2 || {}),
  ]);

  for (const key of allKeys) {
    if (!isEqual(obj1?.[key], obj2?.[key])) {
      differences[key] = { old: obj1?.[key], new: obj2?.[key] };
    }
  }

  return differences;
};

export function normalizeChangesWithNestedObjects(
  changes: Record<string, any>
): Record<string, any> {
  const result: Record<string, any> = {};

  Object.entries(changes).forEach(([key, value]) => {
    if (key.includes('.')) {
      const [parentKey, childKey] = key.split('.');

      if (!parentKey || !childKey) {
        return;
      }

      const normalizedParentKey =
        parentKey.charAt(0).toLowerCase() + parentKey.slice(1);

      const normalizedChildKey =
        childKey.charAt(0).toLowerCase() + childKey.slice(1);

      if (!result[normalizedParentKey]) {
        result[normalizedParentKey] = {};
      }

      if (normalizedChildKey.toLowerCase() !== 'workitemid') {
        result[normalizedParentKey][normalizedChildKey] = value;
      }
    } else {
      const normalizedKey = key.charAt(0).toLowerCase() + key.slice(1);
      result[normalizedKey] = value;
    }
  });

  return result;
}

export const checkUserMutedContracts = (
  userSettings: UserSettings,
  contractId: Contract
): boolean => {
  const mutedContracts =
    userSettings?.dispatcherSettings?.soundSettings?.mutedContracts;
  return Array.isArray(mutedContracts)
    ? mutedContracts.includes(contractId)
    : false;
};

export function getEnumNumericValues<T extends Record<string, string | number>>(
  enumObject: T
): number[] {
  return Object.values(enumObject).filter(
    (value): value is number => typeof value === 'number'
  );
}

export const isTextEditorEmpty = (html: string): boolean => {
  if (!html || html.trim() === '') {
    return true;
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const textContent = doc.body.textContent || '';
    return textContent.trim().length === 0;
  } catch {
    return html.trim() === '';
  }
};
